import React, { useCallback, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Chip } from '@mui/material'
import { parse, stringify } from 'qs'
import DoneIcon from '@mui/icons-material/Done'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun'

import { TableUrlPagination } from '../../../../ui'
import { TableActions } from '../table-actions'
import { useJobsContext } from '../../contexts'
import { JOBS_TABLE_COLUMNS } from './jobs-table.constants'
import { DEFAULT_QUERY } from '../../../campaigns/components/table/campaigns-table.constants'

export const JobsTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const location = useLocation()
  const navigate = useNavigate()
  const {
    data,
    loading,
    search,
    getBatchsTable,
    setSelectedTableRows,
    pagination: { total_reports: totalEntries },
  } = useJobsContext()

  useEffect(() => {
    const searchString = location.search.split('?')[1]
    if (searchString) {
      const urlSearchParams = parse(searchString)
      const searchParams = {
        ...DEFAULT_QUERY,
        ...urlSearchParams,
      }
      getBatchsTable(searchParams)
    } else {
      navigate(`/jobs?${stringify(DEFAULT_QUERY)}`)
    }
  }, [location.search, getBatchsTable, navigate])

  const handleSelectRows = useCallback(
    (rows) => {
      setSelectedTableRows(rows)
    },
    [setSelectedTableRows]
  )

  const formattedColumns = useMemo(
    () =>
      JOBS_TABLE_COLUMNS.map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({ id: `pages.jobs.table.columns.${column.field}.title` }),
          size: column.width,
        }

        if (column.field === 'actions') {
          return {
            ...baseColumn,
            Cell: ({ cell }) => <TableActions cell={cell} />,
            muiTableBodyCellProps: { align: 'center' },
          }
        }
        if (column.field === 'active') {
          return {
            ...baseColumn,
            Cell: ({ cell }) => <input type="checkbox" checked={cell.getValue()} disabled />,
            muiTableBodyCellProps: { align: 'center' },
          }
        }
        if (column.field === 'last_status') {
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              if (value === 'OK') {
                return (
                  <Chip
                    label="success"
                    sx={{ borderColor: '#4caf50', color: '#4caf50' }}
                    variant="outlined"
                    icon={<DoneIcon />}
                  />
                )
              }
              if (value === 'KO') {
                return (
                  <Chip
                    label="Error"
                    sx={{ borderColor: '#d32f2f', color: '#d32f2f' }}
                    variant="outlined"
                    icon={<ErrorOutline />}
                  />
                )
              }
              if (value === 'RUNNING') {
                return (
                  <Chip
                    label="Running"
                    sx={{ borderColor: '#0288d1', color: '#0288d1' }}
                    variant="outlined"
                    icon={<DirectionsRunIcon />}
                  />
                )
              }
              return <Chip label="N/A" color="default" variant="outlined" />
            },
            muiTableBodyCellProps: { align: 'center' },
          }
        }
        if (column.field === 'last_launch') {
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              return value ? new Date(value + 'Z').toLocaleString() : '--'
            },
          }
        }
        return {
          ...baseColumn,
          Cell: ({ cell }) => <span>{cell.getValue() ?? ''}</span>,
        }
      }),
    [formatMessage]
  )

  const processedData = useMemo(() => {
    return data.map((row) => ({
      ...row,
      job_timing: row.job_timing ?? 'N/A',
    }))
  }, [data])

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      draggable: false,
      tableLayout: 'fixed',
      sorting: false,
      fixedColumns: {
        right: 1,
      },
    }),
    []
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onSelectionChange={handleSelectRows}
    />
  )
}
